import { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { PerformanceData } from '@/lib/types';

// Chart typography constants (Recharts requires numeric fontSize values)
const CHART_FONT = {
  AXIS_TICK: 11,      // text-xs equivalent
  AXIS_LABEL: 13,     // text-sm equivalent
  LEGEND: 11,         // text-xs equivalent
} as const;

interface CostPerformanceChartProps {
  data?: PerformanceData[];
  combinedChartData?: PerformanceData[];
  error?: Error | null;
  isLoading: boolean;
  timeRange?: 'daily' | 'weekly';
  campaignName?: string;
  currencySymbol?: string;
}

export const CostPerformanceChart = ({ 
  isLoading, 
  error, 
  data, 
  combinedChartData,
  timeRange = 'weekly', 
  campaignName,
  currencySymbol = "$"
}: CostPerformanceChartProps) => {
  const [activeLines, setActiveLines] = useState<Set<string>>(
    new Set(['cost', 'avgCpc', 'cpa'])
  );


  const handleLegendClick = (e: any) => {
    const clickedMetric = e.dataKey;
    setActiveLines((prev) => {
      // If clicking an already isolated metric, show all metrics
      if (prev.size === 1 && prev.has(clickedMetric)) {
        return new Set(['cost', 'avgCpc', 'cpa']);
      }
      // Otherwise isolate the clicked metric
      return new Set([clickedMetric]);
    });
  };

  const handleLineClick = (metric: string) => {
    setActiveLines((prev) => {
      // If clicking an already isolated metric, return to all metrics
      if (prev.size === 1 && prev.has(metric)) {
        return new Set(['cost', 'avgCpc', 'cpa']);
      }
      // Otherwise isolate the clicked metric
      return new Set([metric]);
    });
  };

  // Find max values for scaling each metric independently
  const maxValues = useMemo(() => {
    if (!data || data.length === 0) {
      return { cost: 1, avgCpc: 1, cpa: 1 };
    }
    
    // Calculate max values, ensuring a minimum of 1 to prevent flat lines at 0
    const values = {
      cost: Math.max(1, ...data.map(item => item.cost || 0)),
      avgCpc: Math.max(1, ...data.map(item => item.avgCpc || 0)),
      cpa: Math.max(1, ...data.map(item => item.cpa || 0))
    };
    
    return values;
  }, [data]);
  
  // Format Y-axis labels consistently to ensure stable width
  const formatYAxisLabel = (value: number) => {
    // Always add currency symbol, always show 2 decimal places
    // Always include at least 1 digit before decimal (0.XX)
    // Pad with spaces if needed to maintain consistent width
    return `${currencySymbol}${value.toFixed(2)}`;
  };
  
  // Format for different metrics
  const formatAvgCpcLabel = (value: number) => {
    return `${currencySymbol}${value.toFixed(2)}`;
  };
  
  const formatCpaLabel = (value: number) => {
    return `${currencySymbol}${value.toFixed(2)}`;
  };

  return (
    <>
      {isLoading && (
        <Card className='p-6'>
          <h3 className='text-lg font-semibold mb-4'>
            Cost Performance ({campaignName ? `${campaignName}, ` : 'All Campaigns, '}
            {timeRange === 'daily' ? 'Last 7 Days' : 'Last 12 Weeks'})
          </h3>
          <div className='h-[300px] flex items-center justify-center bg-gray-50 rounded-md'>
            <div className='text-center'>
              <Skeleton className='h-8 w-40 mx-auto mb-4' />
              <Skeleton className='h-4 w-64 mx-auto' />
            </div>
          </div>
        </Card>
      )}
      {error && (
        <Card className='p-6'>
          <h3 className='text-lg font-semibold mb-4'>
            Cost Performance ({campaignName ? `${campaignName}, ` : 'All Campaigns, '}
            {timeRange === 'daily' ? 'Last 7 Days' : 'Last 12 Weeks'})
          </h3>
          <div className='h-[300px] flex items-center justify-center bg-gray-50 rounded-md'>
            <div className='text-center text-gray-500'>
              <p className='text-lg font-medium mb-2'>Error loading performance data</p>
              <p>Please try again later</p>
            </div>
          </div>
        </Card>
      )}
      {!isLoading && !error && (!data || data.length === 0) && (
        <Card className='p-6'>
          <h3 className='text-lg font-semibold mb-4'>
            Cost Performance ({campaignName ? `${campaignName}, ` : 'All Campaigns, '}
            {timeRange === 'daily' ? 'Last 7 Days' : 'Last 12 Weeks'})
          </h3>
          <div className='h-[300px] flex items-center justify-center bg-gray-50 rounded-md'>
            <div className='text-center text-gray-500'>
              <p className='text-lg font-medium mb-2'>Not enough data</p>
              <p>Performance data will show here when available</p>
            </div>
          </div>
        </Card>
      )}
      {!isLoading && !error && data && data.length > 0 && (
        <Card className='p-6'>
          <h3 className='text-lg font-semibold mb-4'>
            Cost Performance ({campaignName ? `${campaignName}, ` : 'All Campaigns, '}
            {timeRange === 'daily' ? 'Last 7 Days' : 'Last 12 Weeks'})
          </h3>
          <div className='h-[300px]'>
            <ResponsiveContainer width='100%' height='100%'>
              <LineChart data={combinedChartData || data}>
                <CartesianGrid strokeDasharray='3 3' stroke='#E5E7EB' />
                <XAxis
                  dataKey='date'
                  tick={(props) => {
                    const { x, y, payload } = props;
                    // Format date as "DD MMM" (e.g., "27 Apr") for both daily and weekly views
                    const date = new Date(payload.value).toLocaleDateString('en-US', {
                      day: 'numeric',
                      month: 'short'
                    });
                    return (
                      <g transform={`translate(${x},${y})`}>
                        <text 
                          textAnchor="middle"
                          fill="#374151"
                          fontSize={CHART_FONT.AXIS_TICK}
                          transform="rotate(-75)"
                        >
                          <tspan x={-14} dy="0.2em">{date}</tspan>
                        </text>
                      </g>
                    );
                  }}
                  interval={0} // Show all ticks
                  tickMargin={8}
                  padding={{ left: 0, right: 0 }}
                  height={45} // Increase height for angled text with new positioning
                />

                {/* Label Y axis */}
                <YAxis
                  yAxisId='leftLabel'
                  orientation='left'
                  hide={activeLines.size !== 1}
                  tick={false}
                  axisLine={false}
                  width={0} // No width when showing the label
                  label={{
                    value: activeLines.size === 1 ? 
                      String(Array.from(activeLines)[0]).toUpperCase() : '',
                    angle: -90,
                    position: 'insideLeft',
                    fill: '#6B7280',
                    style: { fontSize: CHART_FONT.AXIS_LABEL, fontWeight: 600 },
                    offset: 10, // Adjust label position inside the main axis area
                  }}
                />

                {/* Y axis for cost */}
                <YAxis
                  yAxisId='cost'
                  orientation='left'
                  domain={[0, maxValues.cost]}
                  tick={{ fill: '#374151', fontSize: CHART_FONT.AXIS_TICK }}
                  tickFormatter={formatYAxisLabel}
                  width={60} // Fixed width ensures consistent spacing
                  hide={activeLines.size !== 1 || !activeLines.has('cost')}
                />
                
                {/* Y axis for avgCpc */}
                <YAxis
                  yAxisId='avgCpc'
                  orientation='left'
                  domain={[0, maxValues.avgCpc]}
                  tick={{ fill: '#374151', fontSize: CHART_FONT.AXIS_TICK }}
                  tickFormatter={formatAvgCpcLabel}
                  width={60}
                  hide={activeLines.size !== 1 || !activeLines.has('avgCpc')}
                />
                
                {/* Y axis for cpa */}
                <YAxis
                  yAxisId='cpa'
                  orientation='left'
                  domain={[0, maxValues.cpa]}
                  tick={{ fill: '#374151', fontSize: CHART_FONT.AXIS_TICK }}
                  tickFormatter={formatCpaLabel}
                  width={60}
                  hide={activeLines.size !== 1 || !activeLines.has('cpa')}
                />

                <Tooltip
                  contentStyle={{
                    background: '#fff',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  }}
                  formatter={(value: any, rawName?: string | number) => {
                    const name = rawName !== undefined ? String(rawName) : '';
                    if (!name) return [value, ''];
                    // If this is a previous week value
                    if (name.startsWith('prevWeek')) {
                      const baseLabel = name.replace('prevWeek', '');
                      const formattedValue = (baseLabel === 'Cost' || baseLabel === 'AvgCpc' || baseLabel === 'Cpa') 
                        ? `${currencySymbol}${Number(value).toFixed(2)}` 
                        : value;
                      return [formattedValue, `Last Week ${baseLabel}`];
                    }
                    
                    // For regular metrics
                    let formattedValue;
                    let label;
                    
                    switch (name) {
                      case 'cost':
                        formattedValue = `${currencySymbol}${Number(value).toFixed(2)}`;
                        label = 'Cost';
                        break;
                      case 'avgCpc':
                        formattedValue = `${currencySymbol}${Number(value).toFixed(2)}`;
                        label = 'Cost/Click';
                        break;
                      case 'cpa':
                        formattedValue = `${currencySymbol}${Number(value).toFixed(2)}`;
                        label = 'Cost/Conv';
                        break;
                      default:
                        formattedValue = value;
                        label = name;
                    }
                    
                    return [formattedValue, label];
                  }}
                  labelFormatter={(date) =>
                    new Date(date).toLocaleDateString('en-US', 
                      timeRange === 'daily' 
                        ? { weekday: 'short', month: 'short', day: 'numeric' } 
                        : { year: 'numeric', month: 'short', day: 'numeric' }
                    )
                  }
                />

                <Legend
                  wrapperStyle={{ paddingTop: 20, fontSize: CHART_FONT.LEGEND }}
                  formatter={(value) => {
                    if (value.startsWith('prevWeek')) return null;
                    
                    const labels: Record<string, string> = {
                      cost: 'Cost',
                      avgCpc: 'Cost/Click',
                      cpa: 'Cost/Conv',
                    };
                    return (
                      <span
                        className={`text-sm ${activeLines.has(value) ? 'text-gray-900' : 'text-gray-400'} cursor-pointer`}
                      >
                        {labels[value]}
                      </span>
                    );
                  }}
                  onClick={handleLegendClick}
                />

                {/* Cost Line */}
                <Line
                  yAxisId='cost'
                  type='linear'
                  dataKey='cost'
                  stroke='#A855F7'
                  strokeWidth={2}
                  dot={false}
                  activeDot={{
                    r: 6,
                    onClick: () => handleLineClick('cost'),
                    style: { cursor: 'pointer' },
                  }}
                  strokeOpacity={activeLines.has('cost') ? 1 : 0.2}
                />

                {/* Previous Week Cost Line (only for daily view) */}
                {timeRange === 'daily' && (
                  <Line
                    yAxisId='cost'
                    type='linear'
                    dataKey='prevWeekCost'
                    stroke='#A855F7'
                    strokeWidth={1}
                    strokeDasharray="5 5"
                    dot={false}
                    activeDot={false}
                    strokeOpacity={activeLines.has('cost') ? 0.5 : 0.1}
                  />
                )}

                {/* Average CPC Line */}
                <Line
                  yAxisId='avgCpc'
                  type='linear'
                  dataKey='avgCpc'
                  stroke='#F97316'
                  strokeWidth={2}
                  dot={false}
                  activeDot={{
                    r: 6,
                    onClick: () => handleLineClick('avgCpc'),
                    style: { cursor: 'pointer' },
                  }}
                  strokeOpacity={activeLines.has('avgCpc') ? 1 : 0.2}
                />

                {/* Previous Week AvgCPC Line (only for daily view) */}
                {timeRange === 'daily' && (
                  <Line
                    yAxisId='avgCpc'
                    type='linear'
                    dataKey='prevWeekAvgCpc'
                    stroke='#F97316'
                    strokeWidth={1}
                    strokeDasharray="5 5"
                    dot={false}
                    activeDot={false}
                    strokeOpacity={activeLines.has('avgCpc') ? 0.5 : 0.1}
                  />
                )}

                {/* CPA (Cost per Acquisition) Line */}
                <Line
                  yAxisId='cpa'
                  type='linear'
                  dataKey='cpa'
                  stroke='#3B82F6'
                  strokeWidth={2}
                  dot={false}
                  activeDot={{
                    r: 6,
                    onClick: () => handleLineClick('cpa'),
                    style: { cursor: 'pointer' },
                  }}
                  strokeOpacity={activeLines.has('cpa') ? 1 : 0.2}
                />

                {/* Previous Week CPA Line (only for daily view) */}
                {timeRange === 'daily' && (
                  <Line
                    yAxisId='cpa'
                    type='linear'
                    dataKey='prevWeekCpa'
                    stroke='#3B82F6'
                    strokeWidth={1}
                    strokeDasharray="5 5"
                    dot={false}
                    activeDot={false}
                    strokeOpacity={activeLines.has('cpa') ? 0.5 : 0.1}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}
    </>
  );
};